import { useState, useRef, useEffect } from 'react';
import { Check, X, Edit2, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { format } from 'date-fns';

interface BaseInlineEditProps {
  value: any;
  onSave: (value: any) => Promise<void> | void;
  displayValue?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hideEditIcon?: boolean;
}

// Inline Text Field
interface InlineTextFieldProps extends BaseInlineEditProps {
  multiline?: boolean;
  type?: 'text' | 'number' | 'email' | 'tel';
}

export function InlineTextField({
  value,
  onSave,
  displayValue,
  placeholder = 'Click to edit',
  className,
  disabled = false,
  multiline = false,
  type = 'text',
  hideEditIcon = false,
}: InlineTextFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if ('select' in inputRef.current) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await onSave(editValue);
      setIsEditing(false);
      // Show saved indicator for 3 seconds
      setShowSavedIndicator(true);
      setTimeout(() => setShowSavedIndicator(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-start gap-1">
        {multiline ? (
          <Textarea
            ref={inputRef as any}
            value={editValue || ''}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn('min-h-[60px]', className)}
            disabled={isSaving}
          />
        ) : (
          <Input
            ref={inputRef as any}
            type={type}
            value={editValue || ''}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className={cn('h-8', className)}
            disabled={isSaving}
          />
        )}
        <div className="flex gap-1">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="h-8 w-8 p-0"
          >
            {isSaving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Check className="h-3 w-3" />
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="h-8 w-8 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
        {error && (
          <div className="flex items-center gap-1 px-2 py-1 bg-destructive/10 text-destructive text-xs rounded mt-1">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all',
        'border border-dashed border-transparent hover:border-muted-foreground/30 hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <span className={cn(!value && 'text-muted-foreground')}>
        {displayValue || value || placeholder}
      </span>
      {showSavedIndicator && (
        <span className="flex items-center gap-1 text-green-600 text-xs animate-in fade-in duration-200">
          <Check className="h-3 w-3" />
          Saved
        </span>
      )}
      {!hideEditIcon && !showSavedIndicator && (
        <Edit2 className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
      )}
    </div>
  );
}

// Inline Select Field
interface InlineSelectFieldProps extends BaseInlineEditProps {
  options: { value: string; label: string }[];
}

export function InlineSelectField({
  value,
  onSave,
  options,
  placeholder = 'Select an option',
  className,
  disabled = false,
  hideEditIcon = false,
}: InlineSelectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = async (newValue: string) => {
    if (newValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await onSave(newValue);
      setIsEditing(false);
      // Show saved indicator for 3 seconds
      setShowSavedIndicator(true);
      setTimeout(() => setShowSavedIndicator(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const displayLabel = options.find(opt => opt.value === value)?.label;

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Select
          value={editValue}
          onValueChange={(val) => {
            setEditValue(val);
            handleSave(val);
          }}
          disabled={isSaving}
        >
          <SelectTrigger className={cn('h-8', className)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setEditValue(value);
            setIsEditing(false);
            setError(null);
          }}
          disabled={isSaving}
          className="h-8 w-8 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
        {error && (
          <div className="flex items-center gap-1 px-2 py-1 bg-destructive/10 text-destructive text-xs rounded ml-2">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all',
        'border border-dashed border-transparent hover:border-muted-foreground/30 hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <span className={cn(!value && 'text-muted-foreground')}>
        {displayLabel || placeholder}
      </span>
      {showSavedIndicator && (
        <span className="flex items-center gap-1 text-green-600 text-xs animate-in fade-in duration-200">
          <Check className="h-3 w-3" />
          Saved
        </span>
      )}
      {!hideEditIcon && !showSavedIndicator && (
        <Edit2 className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
      )}
    </div>
  );
}

// Inline Date Field
interface InlineDateFieldProps extends BaseInlineEditProps {
  value: Date | null;
  displayFormat?: string;
}

export function InlineDateField({
  value,
  onSave,
  displayFormat = 'PP',
  placeholder = 'Select a date',
  className,
  disabled = false,
  hideEditIcon = false,
}: InlineDateFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);

  const handleSave = async (newValue: Date | null) => {
    if (newValue?.getTime() === value?.getTime()) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await onSave(newValue);
      setIsEditing(false);
      // Show saved indicator for 3 seconds
      setShowSavedIndicator(true);
      setTimeout(() => setShowSavedIndicator(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <DatePicker
          date={value}
          setDate={(date: Date | null) => handleSave(date)}
          placeholder={placeholder}
          disabled={isSaving}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setIsEditing(false);
            setError(null);
          }}
          disabled={isSaving}
          className="h-8 w-8 p-0"
        >
          <X className="h-3 w-3" />
        </Button>
        {error && (
          <div className="flex items-center gap-1 px-2 py-1 bg-destructive/10 text-destructive text-xs rounded ml-2">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-all',
        'border border-dashed border-transparent hover:border-muted-foreground/30 hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && setIsEditing(true)}
    >
      <span className={cn(!value && 'text-muted-foreground')}>
        {value ? format(value, displayFormat) : placeholder}
      </span>
      {showSavedIndicator && (
        <span className="flex items-center gap-1 text-green-600 text-xs animate-in fade-in duration-200">
          <Check className="h-3 w-3" />
          Saved
        </span>
      )}
      {!hideEditIcon && !showSavedIndicator && (
        <Edit2 className="h-3 w-3 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
      )}
    </div>
  );
}

// Inline Checkbox - Single click toggle
interface InlineCheckboxProps {
  value: boolean;
  onSave: (value: boolean) => Promise<void> | void;
  label: string;
  className?: string;
  disabled?: boolean;
}

export function InlineCheckbox({
  value,
  onSave,
  label,
  className,
  disabled = false,
}: InlineCheckboxProps) {
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = async () => {
    if (disabled || isSaving) return;

    setIsSaving(true);
    try {
      await onSave(!value);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors',
        'hover:bg-muted/50',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onClick={handleToggle}
    >
      <div className={cn(
        'w-4 h-4 rounded border transition-colors',
        value ? 'bg-primary border-primary' : 'border-input',
        isSaving && 'opacity-50'
      )}>
        {value && (
          <Check className="h-3 w-3 text-primary-foreground" />
        )}
      </div>
      <span className="select-none">{label}</span>
      {isSaving && (
        <Loader2 className="h-3 w-3 animate-spin ml-auto" />
      )}
    </div>
  );
}

// Status badge for checklist items
interface InlineStatusSelectProps extends BaseInlineEditProps {
  value: 'pending' | 'in_progress' | 'completed' | 'na';
  hideLabel?: boolean;
}

export function InlineStatusSelect({
  value,
  onSave,
  className,
  disabled = false,
  hideLabel = false,
}: InlineStatusSelectProps) {
  const [isSaving, setIsSaving] = useState(false);

  const statusConfig = {
    completed: { color: 'bg-green-100 text-green-700 hover:bg-green-200', icon: Check, label: 'Complete' },
    in_progress: { color: 'bg-blue-100 text-blue-700 hover:bg-blue-200', icon: Loader2, label: 'In Progress' },
    pending: { color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', icon: null, label: 'Pending' },
    na: { color: 'bg-red-100 text-red-700 hover:bg-red-200', icon: X, label: 'N/A' },
  };

  const handleStatusChange = async (newStatus: typeof value) => {
    if (disabled || isSaving || newStatus === value) return;

    setIsSaving(true);
    try {
      await onSave(newStatus);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {Object.entries(statusConfig).map(([status, config]) => {
        const Icon = config.icon;
        const isActive = value === status;

        return (
          <button
            key={status}
            onClick={() => handleStatusChange(status as typeof value)}
            disabled={disabled || isSaving}
            className={cn(
              'rounded-full px-2 py-1 text-xs font-medium transition-colors flex items-center gap-1',
              isActive ? config.color : 'bg-gray-50 text-gray-400 hover:bg-gray-100',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {Icon && <Icon className="h-3 w-3" />}
            {!hideLabel && config.label}
          </button>
        );
      })}
      {isSaving && (
        <Loader2 className="h-3 w-3 animate-spin ml-1" />
      )}
    </div>
  );
}